import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Checkbox,
  Form,
  Input,
  message,
  Row,
  Col,
  Select,
  Table,
  Tooltip,
  Divider,
  Space
} from "antd";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import "../../../styles/Main.scss";
import { getRequest, postRequest } from "../../../axios";
import { ShowErrorMsg } from "../../../utils/Notifications";
import { authUserData } from "../../../utils/Helper";

const { TextArea } = Input;

const MOCK_RA = {
  raId: "RA-GOTA-240522-001",
  source: "Thaltej Site",
  status: "Pending Buyer Action",
  items: [
    {
      key: "1",
      item: "Cement Bag 50kg",
      qty: 5,
      reason: "Damaged on Site",
      notes: "Bags got wet due to rain...",
    },
  ],
  supplierConfig: "Apex Plumbing Supplies (Auto-filled from Original PO)",
};

const itemColumns = [
  { title: "Item", dataIndex: "item", key: "item" },
  { title: "Qty", dataIndex: "qty", key: "qty", width: 100 },
  { title: "Reason", dataIndex: "reason", key: "reason", width: 200 },
  { title: "Site Notes", dataIndex: "notes", key: "notes" },
];

const ProcurementHubReturnReplacementPlanning = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: idFromPath } = useParams();
  const idFromState =
    location?.state?.id ??
    location?.state?.return_request_id ??
    location?.state?.returnRequestId ??
    null;

  const effectiveId = idFromPath ?? idFromState;
  const [loading, setLoading] = useState(!!effectiveId);
  const [ra, setRa] = useState(location.state?.ra || MOCK_RA);
  const companyId = authUserData()?.company ?? 1;
  const [deliveryLocations, setDeliveryLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [generatingRpo, setGeneratingRpo] = useState(false);

  const [buyerAction, setBuyerAction] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const res = await getRequest(
          `delivery-address/${companyId}/get-address-by-company/`,
          { page: 1, ordering: "id", pagesize: 10 },
        );
        const payload = res?.data;
        const list =
          payload?.results?.data ??
          payload?.data ??
          payload?.results ??
          [];
        setDeliveryLocations(Array.isArray(list) ? list : []);
      } catch (err) {
        ShowErrorMsg(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Failed to load delivery locations",
        );
        setDeliveryLocations([]);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, [companyId]);

  useEffect(() => {
    if (!effectiveId) {
      setRa(location.state?.ra || MOCK_RA);
      setLoading(false);
      return;
    }
    const fetchReturnRequest = async () => {
      try {
        setLoading(true);
        const res = await getRequest(`return-requests/${effectiveId}/`);
        const payload = res?.data;
        const data = payload?.data ?? payload?.results ?? payload;
        if (!data) return;

        // API response: single-item return request (no `items` array)
        // Use intended_destination as the "Reason" column for now.
        const itemsSrc = Array.isArray(data?.items) ? data.items : [];
        const items = itemsSrc.length
          ? itemsSrc.map((it, idx) => ({
              key: String(it?.id ?? idx),
              item: it?.item_name ?? it?.item ?? "—",
              qty: it?.return_qty ?? it?.qty ?? 0,
              reason:
                it?.intended_destination ??
                it?.reason ??
                it?.rejection_reason ??
                "—",
              notes: it?.notes ?? "",
            }))
          : [
              {
                key: String(data?.id ?? "1"),
                item: data?.item_name ?? "—",
                qty: data?.return_qty ?? data?.rejected_qty ?? 0,
                reason:
                  data?.intended_destination ??
                  data?.reason ??
                  data?.rejection_reason ??
                  "—",
                notes: data?.notes ?? "",
              },
            ];

        setRa({
          raId:
            data?.ra_id ??
            data?.return_auth_id ??
            data?.raId ??
            `RA-${data?.id ?? effectiveId}`,
          source:
            data?.source_ref ??
            data?.site_name ??
            data?.source_site_name ??
            data?.source ??
            "—",
          status: data?.status ?? "—",
          items,
          supplierConfig:
            data?.supplier_config ??
            data?.vendor_name ??
            data?.supplier_name ??
            data?.supplierConfig ??
            "—",
        });

        // Pre-fill form with API data when available
        form.setFieldsValue({
          supplierConfig:
            data?.supplier_config ??
            data?.vendor_name ??
            data?.supplier_name ??
            ra?.supplierConfig,
          targetLocation: data?.target_location ?? undefined,
          buyerNotes:
            data?.buyer_notes ?? data?.notes ?? undefined,
        });
      } catch (err) {
        ShowErrorMsg(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Failed to load return request",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchReturnRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveId]);

  const actions = useMemo(
    () => [
      { key: "credit", label: "Issue Credit Note Only" },
      { key: "replacement", label: "Supplier Providing Replacement (FOC)" },
      { key: "reject", label: "Reject Return (Send back to site)" },
    ],
    []
  );

  const actionCheckboxes = actions.map((a) => ({
    label: a.label,
    value: a.key,
  }));

  const disablePrimary = !buyerAction || !form.getFieldValue("targetLocation");
  const buyerActionLabel =
    actions.find((a) => a.key === buyerAction)?.label ?? null;
  const isApprovedAwaitingShipment =
    String(ra?.status || "").trim() === "Approved - Awaiting Shipment";

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip title="Plan return action and supplier replacement">
              <h3>Procurement Hub – Return Action &amp; Replacement Planning</h3>
            </Tooltip>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Procurement Hub</Breadcrumb.Item>
            </Breadcrumb>

          </div>
        </div>
      </div>
      <section className="grid-sec FormSection Container aidocintak">
        <div className="FormBody">
          <div className="stepscontentwrap" style={{ padding: 0 }}>
            <div className="stepsContent" style={{ paddingRight: '5px' }}>
              <div
                className="formwrapper"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {loading && (
                  <div style={{ padding: "10px 0 0 34px" }}>
                    <Alert type="info" showIcon message="Loading return request..." />
                  </div>
                )}
                <div className="po-header-details">
                  <div className="po-row">
                    <strong>Open Return Auth:</strong>
                    {ra.raId}
                  </div>

                  <div className="po-row">
                    <strong>Source:</strong>
                    {ra.source}
                  </div>

                  <div className="po-row">
                    <strong>Status:</strong>
                    {ra.status}
                  </div>
                </div>
                <Divider orientation="left" plain orientationMargin={0} style={{ marginBottom: 0 }}>
                  <div className="returninfo-title"><span className="count">1</span> Review Returned Items &amp; Reason</div>
                </Divider>

                <div className="tablewrp" style={{ paddingLeft: '34px', marginTop: '10px', marginBottom: '16px' }}>
                  <Table
                    rowKey="key"
                    columns={itemColumns}
                    dataSource={ra.items}
                    pagination={false}
                    size="small"
                    scroll={{ x: 700 }}
                  />
                </div>
                <Divider orientation="left" plain orientationMargin={0} style={{ marginBottom: 0 }}>
                  <div className="returninfo-title"><span className="count">2</span> Select Buyer Action &amp; Plan Resolution</div>
                </Divider>

                <div style={{ paddingLeft: '34px', marginTop: '10px', marginBottom: '16px' }}>
                  <Checkbox.Group style={{ fontSize: '14px', fontWeight: 600 }}
                    options={actionCheckboxes}
                    value={buyerAction ? [buyerAction] : []}
                    onChange={(vals) => {
                      const val = vals[vals.length - 1];
                      setBuyerAction(val || null);
                    }}
                  />

                  <div style={{ fontSize: '16px', fontWeight: 600, marginTop: '10px', marginBottom: '10px' }}>
                    Replacement Planning Details (Linked to RPO Generation)
                  </div>
                  <div className="po-header-details" style={{ display: 'inline-flex' }}>
                    <div className="po-row">
                      <strong>Supplier Config:</strong> {ra.supplierConfig}
                    </div>
                  </div>

                  <Form

                    form={form}
                    initialValues={{
                      supplierConfig: ra.supplierConfig,
                      targetLocation: undefined,
                      buyerNotes:
                        "Vendor agreed to replace free of cost. Redirecting to Central Store for future allocation.",
                    }}
                  >
                    <Row gutter={[15]}>
                      <Col
                        xs={16}
                        sm={16}
                        md={6}
                        lg={6}
                        xl={6}
                        className="TextFeild gutter-row"
                      >
                        <Form.Item label="Target Delivery Location for Replacement" name="targetLocation">
                          <Select
                            loading={loadingLocations}
                            options={deliveryLocations.map((d) => ({
                              label:
                                d?.site_name ??
                                d?.company_name ??
                                d?.address_line_1 ??
                                d?.address ??
                                d?.id,
                              value: d?.id,
                            }))}
                            placeholder="Select target location"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item label="Buyer Notes / Instructions" name="buyerNotes">
                          <TextArea rows={3} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>

                </div>
              </div>
            </div>
            <div className="FormFooter">
              <div className="good-tge">


                <Alert
                  type="info"
                  showIcon
                  style={{
                    width: "100%",
                    padding: "5px 10px",
                    borderRadius: 4,
                  }}
                  message={
                    <span>
                      Approval updates will notify Central Procurement and keep the item in Quarantine
                      until carrier assignment and shipment initiation.
                    </span>
                  }
                />
              </div>
              {!isApprovedAwaitingShipment && (
                <Space style={{ justifyContent: "flex-end" }}>
                  <Button
                    type="primary"
                    className="SaveGradiantBtn"
                    disabled={disablePrimary}
                    loading={generatingRpo}
                    onClick={() => {
                      const run = async () => {
                        if (!effectiveId) {
                          ShowErrorMsg("Return request ID is missing.");
                          return;
                        }
                        if (!buyerActionLabel) {
                          ShowErrorMsg("Please select buyer action.");
                          return;
                        }
                        const values = form.getFieldsValue();
                        const targetLocationId = values?.targetLocation ?? null;
                        const loc = deliveryLocations.find(
                          (d) => String(d?.id) === String(targetLocationId),
                        );
                        const targetDeliveryLocation =
                          loc?.site_name ??
                          loc?.company_name ??
                          loc?.address_line_1 ??
                          loc?.address ??
                          (targetLocationId != null ? String(targetLocationId) : "");

                        const payload = {
                          buyer_action: buyerActionLabel,
                          supplier_config: values?.supplierConfig ?? ra?.supplierConfig ?? "",
                          target_delivery_location: targetDeliveryLocation,
                          buyer_notes: values?.buyerNotes ?? "",
                        };

                        try {
                          setGeneratingRpo(true);
                          const res = await postRequest(
                            `return-requests/${effectiveId}/generate_rpo/`,
                            payload,
                          );
                          message.success(
                            res?.data?.message ?? "RPO generated successfully.",
                          );
                          navigate("/return-requests-list");
                        } catch (err) {
                          ShowErrorMsg(
                            err?.response?.data?.error ||
                              err?.response?.data?.message ||
                              err?.message ||
                              "Failed to generate RPO",
                          );
                        } finally {
                          setGeneratingRpo(false);
                        }
                      };
                      run();
                    }}
                  >
                    Generate Zero-Value Replacement PO (RPO)
                  </Button>
                </Space>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProcurementHubReturnReplacementPlanning;

